import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge, ListGroup, InputGroup } from 'react-bootstrap';
import { FaUser, FaWallet, FaStar, FaArrowLeft, FaSave, FaEye, FaShoppingCart, FaPlus } from 'react-icons/fa';
import userService from '../services/userService';
import orderService from '../services/orderService';
import analyticsService from '../services/analyticsService';

function UserDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [viewedProducts, setViewedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'subscriber',
    email_verified: false
  });

  const [balanceData, setBalanceData] = useState({
    balance: 0,
    points: 0
  });

  const [addBalanceAmount, setAddBalanceAmount] = useState('');
  const [addPointsAmount, setAddPointsAmount] = useState('');

  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch user details
      const userData = await userService.getById(id);
      setUser(userData);
      setFormData({
        username: userData.username,
        email: userData.email,
        password: '',
        role: userData.role,
        email_verified: userData.email_verified || false
      });
      setBalanceData({
        balance: userData.balance || 0,
        points: userData.points || 0
      });

      // Fetch user orders
      const ordersData = await orderService.getAll({ user_id: id, per_page: 10 });
      setOrders(ordersData.orders || []);

      // Fetch viewed products
      try {
        const viewsData = await analyticsService.getUserViewHistory(id);
        setViewedProducts(viewsData.views || []);
      } catch (err) {
        console.error('Failed to load view history:', err);
      }
    } catch (err) {
      setError('Failed to load user details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await userService.update(id, updateData);
      setSuccess('User details updated successfully');
      fetchUserDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update user details');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddBalance = async () => {
    const amount = parseFloat(addBalanceAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    try {
      const newBalance = balanceData.balance + amount;
      await userService.update(id, { balance: newBalance });
      setSuccess(`Added $${amount} to balance`);
      setAddBalanceAmount('');
      fetchUserDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add balance');
      console.error(err);
    }
  };

  const handleAddPoints = async () => {
    const amount = parseInt(addPointsAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid points amount');
      return;
    }
    try {
      const newPoints = balanceData.points + amount;
      await userService.update(id, { points: newPoints });
      setSuccess(`Added ${amount} points`);
      setAddPointsAmount('');
      fetchUserDetails();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add points');
      console.error(err);
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      completed: 'success',
      pending: 'warning',
      canceled: 'danger'
    };
    return variants[status] || 'secondary';
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'primary',
      designer: 'secondary',
      subscriber: 'light'
    };
    return variants[role] || 'light';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <Container fluid className="px-4">
        <Alert variant="danger">User not found</Alert>
        <Button variant="secondary" onClick={() => navigate('/users')}>
          <FaArrowLeft className="me-2" />
          Back to Users
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4">
      <div className="d-flex align-items-center mb-4">
        <Button variant="link" className="text-dark p-0 me-3" onClick={() => navigate('/users')}>
          <FaArrowLeft size={20} />
        </Button>
        <div>
          <h3 className="mb-1 fw-bold">{user.username}</h3>
          <p className="text-muted mb-0">{user.email}</p>
        </div>
        <Badge bg={getRoleBadge(user.role)} className="ms-3">{user.role}</Badge>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Balance Cards */}
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-success bg-opacity-10 rounded me-3">
                    <FaWallet className="text-success" size={24} />
                  </div>
                  <div>
                    <p className="text-muted text-uppercase small mb-1">Account Balance</p>
                    <h3 className="fw-bold mb-0">${balanceData.balance.toFixed(2)}</h3>
                  </div>
                </div>
              </div>
              <InputGroup size="sm">
                <Form.Control
                  type="number"
                  step="0.01"
                  placeholder="Amount to add"
                  value={addBalanceAmount}
                  onChange={(e) => setAddBalanceAmount(e.target.value)}
                />
                <Button variant="success" onClick={handleAddBalance}>
                  <FaPlus className="me-1" />
                  Add
                </Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="d-flex align-items-center">
                  <div className="p-3 bg-warning bg-opacity-10 rounded me-3">
                    <FaStar className="text-warning" size={24} />
                  </div>
                  <div>
                    <p className="text-muted text-uppercase small mb-1">Reward Points</p>
                    <h3 className="fw-bold mb-0">{balanceData.points}</h3>
                  </div>
                </div>
              </div>
              <InputGroup size="sm">
                <Form.Control
                  type="number"
                  placeholder="Points to add"
                  value={addPointsAmount}
                  onChange={(e) => setAddPointsAmount(e.target.value)}
                />
                <Button variant="warning" onClick={handleAddPoints}>
                  <FaPlus className="me-1" />
                  Add
                </Button>
              </InputGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Account Details */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex align-items-center mb-3">
            <FaUser className="text-primary me-2" />
            <h5 className="fw-semibold mb-0">Account Details</h5>
          </div>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password (leave empty to keep current)</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter new password"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="subscriber">Subscriber</option>
                    <option value="designer">Designer</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="email_verified"
                    label="Email Verified"
                    checked={formData.email_verified}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex gap-2">
              <Button variant="primary" type="submit" disabled={saving}>
                <FaSave className="me-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/users')}>
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Row className="g-4">
        {/* User Orders */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaShoppingCart className="text-primary me-2" />
                  <h5 className="fw-semibold mb-0">Recent Orders</h5>
                </div>
                <Badge bg="secondary" pill>{orders.length}</Badge>
              </div>
              {orders.length === 0 ? (
                <p className="text-muted mb-0">No orders yet</p>
              ) : (
                <ListGroup variant="flush">
                  {orders.map((order) => (
                    <ListGroup.Item key={order.id} className="px-0 py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">Order #{order.id}</div>
                          <small className="text-muted">
                            {new Date(order.created_at).toLocaleDateString()}
                          </small>
                        </div>
                        <div className="text-end">
                          <div className="fw-semibold">${order.display_total}</div>
                          <Badge bg={getStatusVariant(order.status)} className="mt-1">
                            {order.display_status}
                          </Badge>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              {orders.length > 0 && (
                <Button
                  variant="link"
                  className="w-100 mt-2"
                  onClick={() => navigate(`/orders?user_id=${id}`)}
                >
                  View All Orders
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Viewed Products */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <FaEye className="text-primary me-2" />
                  <h5 className="fw-semibold mb-0">Recently Viewed Products</h5>
                </div>
                <Badge bg="secondary" pill>{viewedProducts.length}</Badge>
              </div>
              {viewedProducts.length === 0 ? (
                <p className="text-muted mb-0">No products viewed yet</p>
              ) : (
                <ListGroup variant="flush">
                  {viewedProducts.slice(0, 5).map((view, index) => (
                    <ListGroup.Item key={index} className="px-0 py-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div className="fw-semibold">{view.product?.title || 'Product'}</div>
                          <small className="text-muted">
                            {new Date(view.viewed_at).toLocaleDateString()}
                          </small>
                        </div>
                        <Badge bg="light" text="dark">{view.view_count || 1} views</Badge>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="mb-3"></div>
    </Container>
  );
}

export default UserDetailsPage;
